import { itemName, mapName, nameKey } from './worldFormat'
import { derived, itemRoutes, routeKindLabel } from './worldQueries'
import { invasionChecks, targetWithheld, withheldPolicies } from './invasionValidation'
import type { InvasionCheck } from './invasionValidation'
import type { DropRoute, DropRule, Spawn, WorldData } from '../types/world'

/*
 * Resuelve "dónde conseguir" un ítem siguiendo solo relaciones documentadas por ID.
 * Fuente principal: route_targets.csv (route_id → monster_id + map_id, con is_indirect,
 * event_id y location_kind). Se consulta SIEMPRE primero, para todos los tipos de ruta.
 * Respaldo cuando una ruta no tiene objetivos ahí:
 *   item_drop_rules → item_drop_rule_targets / monster_filter
 *   bag.via_item_ids → rutas de esa caja
 *   event_direct_items (monstruo + mapa)
 * Nunca deduce monstruos por nivel, por mapa compartido ni por item_rate_divisor.
 * Un objetivo documentado = combinación posible; no es drop garantizado ni prueba en juego.
 */

export type MonsterTarget = {
  monsterId: number
  mapId?: number
  spawns: Spawn[]
  eventId?: string
  locationKind?: string
  /** El monstruo no entrega el ítem final directamente. */
  isIndirect?: boolean
}

export type EventRewardMap = { mapId: number; eventId?: string; locationKind?: string }

/**
 * Contexto de UN objetivo (no de toda la ruta): un monstruo en mapa normal es drop ordinario;
 * un monstruo que aparece dentro de un evento/invasión es "monstruo dentro del evento".
 * Ninguno de los dos es un premio por ganar el evento (eso es location_kind = event_reward sin monstruo).
 */
export type TargetContext = 'ordinary' | 'event'
export function targetContext(t: { eventId?: string; locationKind?: string }): TargetContext {
  const k = (t.locationKind ?? '').toLowerCase()
  if (k === 'ordinary' || k === 'normal' || k === 'permanent') return 'ordinary'
  if (t.eventId || /event|invasion/.test(k)) return 'event'
  return 'ordinary'
}

/** Estado de una ruta. Una ruta desactivada NO hace imposible al ítem: puede haber otras activas. */
export type SourceStatus = 'active' | 'partial' | 'disabled'

export type AccountRate = { level: number; label: string; rate?: number }

export type ResolvedSource = {
  key: string
  route: DropRoute
  kind: string
  title: string
  status: SourceStatus
  conditions?: string
  playerProbability?: string
  playerStatusNote?: string
  accountRates?: AccountRate[]
  targets: MonsterTarget[]
  /** Premios de evento sin monstruo (location_kind = event_reward). */
  eventMaps: EventRewardMap[]
  eventMapId?: number
  via?: { itemId: string; name: string; sources: ResolvedSource[] }[]
  /** De dónde salieron los monstruos. */
  targetOrigin?: 'route_targets' | 'rule_targets' | 'monster_filter' | 'event_direct'
  runtimeTested?: boolean
  pending: boolean
  /** Motivo concreto si queda pendiente (panel). */
  pendingReason?: string
  techNotes: string[]
  /** Etiqueta para el jugador según cómo se obtiene realmente (no según el término técnico "bag"). */
  label?: string
  /** Asociaciones con invasiones retenidas por conflicto o falta de lista vigente (solo panel). */
  withheld?: InvasionCheck[]
  /** Toda la ruta queda retenida: no se muestra al jugador. */
  blocked?: boolean
  /** Filas originales de route_targets.csv para la ruta, ANTES de cualquier filtro de publicación. */
  rawTargetRows: number
  /** Filas originales retenidas por revisión (invasiones). Existen en el archivo; no se publican. */
  heldTargetRows: number
  /** Objetivos retenidos por revisión (únicos monstruo/mapa/evento). */
  heldTargets: MonsterTarget[]
  /**
   * Mecanismo de una ruta de bolsa, según 17_bag_triggers y los objetivos:
   * - box: hay que abrir un ítem (via_item_ids o activador por ítem).
   * - internal: recompensa interna al derrotar al monstruo (activador por monstruo u objetivos directos).
   */
  mechanism?: 'box' | 'internal'
}

const pct = (n: number, scale: number) => {
  const p = (n / scale) * 100
  return `${p.toLocaleString('es-ES', { maximumFractionDigits: p < 0.01 ? 4 : 3 })} %`
}

function accountLabel(w: WorldData, level: number): string {
  const r = w.rates.find((x) => x.accountLevel === level && x.accountName)
  return r?.accountName ? `${r.accountName}` : `Cuenta nivel ${level}`
}

/** rate_al0..3 por tipo de cuenta. Todo en cero = regla desactivada; algunos en cero = solo ciertas cuentas. */
function ruleRates(w: WorldData, rule: DropRule) {
  const accountRates: AccountRate[] = rule.rates.map((rate, level) => ({ level, label: accountLabel(w, level), rate }))
  const defined = accountRates.filter((a) => a.rate !== undefined)
  const scale = rule.rateScale
  if (!defined.length || scale === undefined) return { status: 'active' as SourceStatus, accountRates: undefined, player: undefined, tech: 'La regla no define tasas por tipo de cuenta o falta rate_scale.' }
  const positive = defined.filter((a) => (a.rate ?? 0) > 0)
  const unique = Array.from(new Set(defined.map((a) => a.rate)))
  if (!positive.length) {
    return { status: 'disabled' as SourceStatus, accountRates, player: undefined, tech: `${rule.id}: rate_al0..rate_al3 = 0 → esta regla está desactivada. No afecta a las demás rutas del ítem.` }
  }
  if (positive.length < defined.length) {
    return {
      status: 'partial' as SourceStatus,
      accountRates,
      player: `Solo para: ${positive.map((a) => `${a.label} (${pct(a.rate!, scale)})`).join(', ')}`,
      tech: `${rule.id}: tasas por cuenta ${defined.map((a) => `AL${a.level}=${a.rate}`).join(', ')} / ${scale}.`,
    }
  }
  return {
    status: 'active' as SourceStatus,
    accountRates: unique.length > 1 ? accountRates : undefined,
    player:
      unique.length === 1
        ? `Tirada de admisión: ${pct(unique[0]!, scale)}`
        : `Tirada de admisión por cuenta: ${defined.map((a) => `${a.label} ${pct(a.rate!, scale)}`).join(' · ')}`,
    tech: `${rule.id}: ${defined.map((a) => `AL${a.level}=${a.rate}`).join(', ')} / ${scale}. ${rule.probabilitySemantics ?? ''}`.trim(),
  }
}

const byLevel = (w: WorldData) => (a: MonsterTarget, b: MonsterTarget) => (w.monsters.get(a.monsterId)?.level ?? 0) - (w.monsters.get(b.monsterId)?.level ?? 0)

/**
 * Apariciones de un monstruo en un mapa, respetando el contexto:
 * - Objetivo de evento/invasión → solo apariciones de ese mismo evento.
 * - Objetivo ordinario → solo apariciones permanentes (sin event_id). Una aparición de invasión
 *   no se presenta como lugar de drop ordinario.
 * Si no queda ninguna, la ficha muestra monstruo y mapa con "Ubicación exacta no documentada".
 */
function spawnsFor(w: WorldData, monsterId: number, mapId?: number, eventId?: string): Spawn[] {
  const all = (w.idx.spawnsByMonster.get(monsterId) ?? []).filter((s) => mapId === undefined || s.mapId === mapId)
  return eventId ? all.filter((s) => s.eventId === eventId) : all.filter((s) => !s.eventId)
}

/**
 * Objetivos de route_targets.csv para una ruta. Se leen TODAS las filas originales y después se
 * separan las retenidas por revisión: el recuento original nunca depende del filtro de publicación.
 */
function routeTargetsFor(w: WorldData, route: DropRoute) {
  const all = w.idx.routeTargetsByRoute.get(route.id) ?? []
  const list = all.filter((t) => !targetWithheld(w, route, t))
  const heldRows = all.filter((t) => targetWithheld(w, route, t))
  const heldSeen = new Set<string>()
  const heldTargets: MonsterTarget[] = []
  heldRows.forEach((t) => {
    if (t.monsterId === undefined) return
    const k = `${t.monsterId}|${t.mapId ?? ''}|${t.eventId ?? ''}`
    if (heldSeen.has(k)) return
    heldSeen.add(k)
    heldTargets.push({ monsterId: t.monsterId, mapId: t.mapId, eventId: t.eventId, locationKind: t.locationKind, isIndirect: t.isIndirect, spawns: spawnsFor(w, t.monsterId, t.mapId, t.eventId) })
  })
  const seen = new Set<string>()
  const targets: MonsterTarget[] = []
  const eventMaps: EventRewardMap[] = []
  let unresolved = 0
  list.forEach((t) => {
    if (t.monsterId === undefined) {
      if (t.mapId === undefined) {
        unresolved++
        return
      }
      const k = `map|${t.mapId}|${t.eventId ?? ''}`
      if (seen.has(k)) return
      seen.add(k)
      eventMaps.push({ mapId: t.mapId, eventId: t.eventId, locationKind: t.locationKind })
      return
    }
    const k = `${t.monsterId}|${t.mapId ?? ''}|${t.eventId ?? ''}|${t.isIndirect ? 1 : 0}`
    if (seen.has(k)) return
    seen.add(k)
    targets.push({
      monsterId: t.monsterId,
      mapId: t.mapId,
      eventId: t.eventId,
      locationKind: t.locationKind,
      isIndirect: t.isIndirect,
      spawns: spawnsFor(w, t.monsterId, t.mapId, t.eventId),
    })
  })
  targets.sort(byLevel(w))
  const indirect = targets.filter((t) => t.isIndirect).length
  const ctx = all.reduce<Record<string, number>>((a, t) => {
    const k = t.monsterId === undefined ? 'event_reward' : targetContext(t)
    a[k] = (a[k] ?? 0) + 1
    return a
  }, {})
  const distinct = new Set(all.map((t) => t.monsterId).filter((x) => x !== undefined)).size
  return {
    rawRows: all.length,
    heldRows: heldRows.length,
    heldTargets,
    rows: list.length,
    targets,
    eventMaps,
    unresolved,
    tech: all.length
      ? `route_targets.csv (original): ${all.length} filas · contexto ${Object.entries(ctx).map(([k, n]) => `${k}=${n}`).join(', ')} · ${distinct} monstruos distintos · retenidas por revisión ${heldRows.length} · publicables ${list.length} → ${targets.length} monstruo/mapa (${targets.length - indirect} directos, ${indirect} indirectos), ${eventMaps.length} mapas de premio de evento${unresolved ? `, ${unresolved} filas sin monstruo ni mapa` : ''}.`
      : 'route_targets.csv: 0 filas para esta ruta.',
  }
}

function ruleTargets(w: WorldData, rule: DropRule): { targets: MonsterTarget[]; origin: 'rule_targets' | 'monster_filter' } {
  const list = w.idx.targetsByRule.get(rule.id) ?? []
  const seen = new Set<string>()
  const out: MonsterTarget[] = []
  list.forEach((t) => {
    if (t.monsterId === undefined) return
    const k = `${t.monsterId}|${t.mapId ?? ''}`
    if (seen.has(k)) return
    seen.add(k)
    out.push({ monsterId: t.monsterId, mapId: t.mapId, spawns: spawnsFor(w, t.monsterId, t.mapId) })
  })
  if (!out.length && rule.monsterFilter !== undefined) {
    out.push({ monsterId: rule.monsterFilter, mapId: rule.mapFilter, spawns: spawnsFor(w, rule.monsterFilter, rule.mapFilter) })
    return { targets: out, origin: 'monster_filter' }
  }
  out.sort(byLevel(w))
  return { targets: out, origin: 'rule_targets' }
}

function ruleFilterText(w: WorldData, rule: DropRule): string {
  const parts: string[] = []
  if (rule.mapFilter !== undefined) parts.push(`map_filter=${rule.mapFilter} (${mapName(w, rule.mapFilter)})`)
  if (rule.monsterLevelMin !== undefined || rule.monsterLevelMax !== undefined) parts.push(`monster_level ${rule.monsterLevelMin ?? '?'}–${rule.monsterLevelMax ?? '?'}`)
  return parts.length ? parts.join(', ') : 'sin filtros'
}

/** Etiqueta para el jugador según el contexto real de sus objetivos (no según el término técnico). */
function playerLabel(s: ResolvedSource): string {
  if (s.kind === 'quest') return 'Misión'
  if (s.via && s.via.length) return 'Al abrir una caja'
  if (s.targets.length) {
    const evt = s.targets.filter((t) => targetContext(t) === 'event')
    if (evt.length === s.targets.length) {
      return evt.every((t) => t.eventId?.startsWith('invasion:')) ? 'Monstruo de invasión' : 'Monstruo dentro de un evento'
    }
    if (s.mechanism === 'internal') return 'Recompensa al derrotar al monstruo'
    return 'Drop del monstruo'
  }
  if (s.eventMaps.length || s.eventMapId !== undefined) return 'Premio de evento'
  return routeKindLabel[s.kind] ?? s.kind
}

const verdictNote: Record<string, string> = { conflict: 'contradicho por la información aprobada', unlisted: 'sin lista vigente o pendiente de aprobación editorial', authorized: 'autorizado' }

/** Aplica la revisión de invasiones y la etiqueta para el jugador. La ruta y sus filas originales se conservan. */
function finalize(w: WorldData, s: ResolvedSource): ResolvedSource {
  const held = withheldPolicies(w, s.route)
  let out = s
  if (held.length) {
    const bagHeld = s.route.bagId !== undefined && held.some((c) => c.policy.bagIds.includes(s.route.bagId!))
    const blocked = bagHeld || (s.heldTargetRows > 0 && s.targets.length === 0 && s.eventMaps.length === 0 && !s.via?.length)
    out = {
      ...s,
      withheld: held,
      blocked,
      pending: blocked ? false : s.pending,
      pendingReason: blocked ? undefined : s.pendingReason,
      techNotes: [...s.techNotes, ...held.map((c) => `EN REVISIÓN · ${c.policy.name} · ${verdictNote[c.verdict]}: ${c.reason}`)],
    }
  }
  return { ...out, label: playerLabel(out) }
}

export function resolveItemSources(w: WorldData, id: string, depth = 0): ResolvedSource[] {
  return resolveRaw(w, id, depth).map((s) => finalize(w, s))
}

/* ── Vista del jugador: todas las rutas documentadas, evaluadas una a una ─────
 * Cada ruta se evalúa por sus propios datos:
 * - Retenida por conflicto entre fuentes (invasionPolicies) → no se muestra; solo esa asociación.
 * - Desactivada (verified_possible = False o tasas en cero) → "No disponibles ahora".
 * - Sin objetivos ni caja con origen → "Ubicación de obtención pendiente de confirmar".
 * - Normal / Excellent / Ancient y variantes se separan; nunca se mezclan.
 * El mismo criterio se aplica en ítems, monstruos, cajas y eventos.
 */

export type SourceGroup = 'drop' | 'event' | 'box' | 'quest' | 'other'
export type Quality = 'normal' | 'excellent' | 'ancient' | 'other'

export const groupLabel: Record<SourceGroup, string> = {
  drop: 'Drop de monstruos',
  event: 'Invasiones y eventos',
  box: 'Desde cajas',
  quest: 'Misiones',
  other: 'Otras formas de obtención',
}

export const qualityLabel: Record<Quality, string> = { normal: 'Versión normal', excellent: 'Excellent', ancient: 'Ancient', other: 'Otra calidad' }

export function routeQuality(r: DropRoute): Quality {
  const q = (r.quality ?? '').toLowerCase().trim()
  if (!q || q === 'normal' || q === 'common') return 'normal'
  if (q.startsWith('exc')) return 'excellent'
  if (q.startsWith('anc')) return 'ancient'
  return 'other'
}

/** Grupo por el contexto de los objetivos. Una ruta con contextos mezclados se divide antes (playerItemSources). */
export function sourceGroup(s: ResolvedSource): SourceGroup {
  if (s.kind === 'quest') return 'quest'
  if ((s.via && s.via.length) || (s.targets.length > 0 && s.targets.every((t) => t.isIndirect))) return 'box'
  if (s.targets.length) return s.targets.every((t) => targetContext(t) === 'event') ? 'event' : 'drop'
  if (s.eventMaps.length || s.eventMapId !== undefined || s.kind === 'event' || s.kind === 'event_direct') return 'event'
  return 'other'
}

function groupTitle(s: ResolvedSource, g: SourceGroup, q: Quality): string {
  const qs = q === 'normal' ? '' : ` ${qualityLabel[q]}`
  if (g === 'drop') return s.mechanism === 'internal' ? `Recompensa al derrotar al monstruo${qs}` : q === 'normal' ? 'Drop de monstruos' : `Drop ${qualityLabel[q]}`
  if (g === 'quest') return 'Material de misión'
  if (g === 'box') return 'Dentro de una caja'
  if (g === 'event') return s.targets.length ? `Monstruos dentro de eventos o invasiones${qs}` : `Premio de evento o invasión${qs}`
  return 'Forma de obtención'
}

/** Divide una ruta cuyos objetivos mezclan mapas normales y eventos en dos entradas, una por contexto. */
function splitByContext(s: ResolvedSource): ResolvedSource[] {
  if (s.via?.length || s.targets.length < 2) return [s]
  const ord = s.targets.filter((t) => targetContext(t) === 'ordinary')
  const evt = s.targets.filter((t) => targetContext(t) === 'event')
  if (!ord.length || !evt.length) return [s]
  return [
    { ...s, key: `${s.key}#ordinario`, targets: ord, eventMaps: [] },
    { ...s, key: `${s.key}#evento`, targets: evt },
  ].map((x) => ({ ...x, label: playerLabel(x) }))
}

/**
 * Rutas para el jugador: todas salvo las retenidas por revisión. Incluye las desactivadas
 * (la ficha las muestra aparte) y las pendientes (con aviso). Cada objetivo se clasifica por su contexto.
 */
export function playerItemSources(w: WorldData, id: string): ResolvedSource[] {
  return resolveItemSources(w, id)
    .filter((s) => !s.blocked)
    .flatMap(splitByContext)
    .map((s) => {
      const g = sourceGroup(s)
      const natural = s.kind === 'natural'
      return {
        ...s,
        title: groupTitle(s, g, routeQuality(s.route)),
        // La descripción genérica del drop natural ("depende de la tasa global…") no aporta al jugador.
        conditions: natural ? undefined : s.conditions,
        playerProbability: natural ? undefined : s.playerProbability,
      }
    })
}

/** ¿La ruta (y este objetivo) se muestran al jugador en la ficha del monstruo? */
export function playerRouteVisible(w: WorldData, r: DropRoute, t: { eventId?: string; monsterId?: number }): boolean {
  if (routeDisabled(w, r) || targetWithheld(w, r, t)) return false
  // Bolsa retenida por completo (p. ej. Red Dragon, bag 2): tampoco se muestra desde el monstruo.
  return !(r.bagId !== undefined && withheldPolicies(w, r).some((c) => c.policy.bagIds.includes(r.bagId!)))
}

/** Invasión aprobada expresamente (para el panel). */
export function approvedInvasionRoute(w: WorldData, r: DropRoute): boolean {
  const checks = invasionChecks(w, r)
  return checks.length > 0 && checks.every((c) => c.verdict === 'authorized')
}

function resolveRaw(w: WorldData, id: string, depth: number): ResolvedSource[] {
  const routes = itemRoutes(w, id)
  const item = w.items.get(w.variants.get(id)?.itemId ?? id)

  return routes.map((r): ResolvedSource => {
    const rt = routeTargetsFor(w, r)
    // "Tiene objetivos" se decide con las filas ORIGINALES; retenidas por revisión siguen existiendo.
    const hasRt = rt.rawRows > 0 || rt.targets.length > 0 || rt.eventMaps.length > 0 || rt.heldRows > 0
    const base: ResolvedSource = {
      rawTargetRows: rt.rawRows,
      heldTargetRows: rt.heldRows,
      heldTargets: rt.heldTargets,
      key: r.id,
      route: r,
      kind: r.kind,
      title: r.description ?? 'Fuente sin descripción',
      status: r.verifiedPossible === false ? 'disabled' : 'active',
      conditions: r.conditions,
      playerProbability: r.probabilityDescription,
      playerStatusNote: r.verifiedPossible === false ? 'No disponible en la configuración actual.' : undefined,
      targets: rt.targets,
      eventMaps: rt.eventMaps,
      targetOrigin: hasRt ? 'route_targets' : undefined,
      runtimeTested: r.runtimeTested,
      pending: !hasRt,
      techNotes: [
        `${r.id} · kind=${r.kind}${r.ruleId ? ` · rule_id=${r.ruleId}` : ''}${r.bagId ? ` · bag_id=${r.bagId}` : ''}${r.eventDirectItemId ? ` · event_direct_item_id=${r.eventDirectItemId}` : ''}${r.viaItemIds.length ? ` · via_item_ids=${r.viaItemIds.join('|')}` : ''} · verified_possible=${r.verifiedPossible ?? '—'} · runtime_tested=${r.runtimeTested ?? '—'}`,
        rt.tech,
      ],
    }
    if (r.verifiedPossible === false) base.techNotes.push('drop_routes.verified_possible = False (solo afecta a esta ruta).')

    if (r.kind === 'rule') {
      const rule = r.ruleId ? w.rules.get(r.ruleId) : undefined
      if (!rule) {
        return hasRt
          ? { ...base, techNotes: [...base.techNotes, `AVISO: ${r.ruleId ?? 'rule_id vacío'} no existe en item_drop_rules.csv; se usan los objetivos de route_targets.csv.`] }
          : { ...base, pendingReason: `${r.ruleId ?? 'rule_id vacío'} no existe en item_drop_rules.csv y la ruta no tiene filas en route_targets.csv.`, techNotes: [...base.techNotes, `FALTA: ${r.ruleId ?? 'rule_id vacío'} no existe en item_drop_rules.csv.`] }
      }
      const rates = ruleRates(w, rule)
      const fb = hasRt ? undefined : ruleTargets(w, rule)
      const targets = hasRt ? rt.targets : fb!.targets
      const status: SourceStatus = base.status === 'disabled' ? 'disabled' : rates.status
      return {
        ...base,
        status,
        playerProbability: rates.player ?? base.playerProbability,
        playerStatusNote: status === 'disabled' ? 'No disponible en la configuración actual.' : status === 'partial' ? 'Solo para ciertos tipos de cuenta.' : base.playerStatusNote,
        accountRates: rates.accountRates,
        targets,
        targetOrigin: hasRt ? 'route_targets' : targets.length ? fb!.origin : undefined,
        pending: targets.length === 0 && rt.eventMaps.length === 0,
        pendingReason: targets.length || rt.eventMaps.length ? undefined : `Ni route_targets.csv ni item_drop_rule_targets.csv tienen objetivos para ${r.id} / ${rule.id}, y la regla no tiene monster_filter (filtros: ${ruleFilterText(w, rule)}).`,
        techNotes: [...base.techNotes, rates.tech, `Filtros de la regla: ${ruleFilterText(w, rule)}.`, ...(fb && targets.length ? [`Respaldo: ${targets.length} monstruos desde ${fb.origin === 'monster_filter' ? 'item_drop_rules.monster_filter' : 'item_drop_rule_targets.csv'}.`] : [])],
      }
    }

    if (r.kind === 'event_direct') {
      const e = r.eventDirectItemId ? w.idx.eventItemById.get(r.eventDirectItemId) : undefined
      if (!e) {
        return hasRt
          ? base
          : { ...base, pendingReason: `${r.eventDirectItemId ?? 'event_direct_item_id vacío'} no existe en event_direct_items.csv.`, techNotes: [...base.techNotes, `FALTA: ${r.eventDirectItemId ?? 'event_direct_item_id vacío'} no existe en event_direct_items.csv.`] }
      }
      const chance =
        e.chanceByServer && e.chanceDenominator
          ? `Probabilidad: ${Object.entries(e.chanceByServer).map(([s, n]) => `${s} ${pct(n, e.chanceDenominator!)}`).join(' · ')}`
          : base.playerProbability
      const targets = hasRt ? rt.targets : e.monsterId !== undefined ? [{ monsterId: e.monsterId, mapId: e.mapId, eventId: e.eventId, spawns: spawnsFor(w, e.monsterId, e.mapId, e.eventId) }] : []
      const disabled = base.status === 'disabled' || e.active === false
      return {
        ...base,
        status: disabled ? 'disabled' : 'active',
        playerStatusNote: disabled ? 'No disponible en la configuración actual.' : undefined,
        conditions: e.condition ?? base.conditions,
        playerProbability: chance,
        targets,
        targetOrigin: hasRt ? 'route_targets' : targets.length ? 'event_direct' : undefined,
        eventMapId: !hasRt && e.monsterId === undefined ? e.mapId : undefined,
        pending: targets.length === 0 && rt.eventMaps.length === 0 && e.mapId === undefined,
        techNotes: [...base.techNotes, `event_direct_items ${e.id}: active=${e.active ?? '—'}, monster_id=${e.monsterId ?? '—'}, map_id=${e.mapId ?? '—'}.`],
      }
    }

    if (r.kind === 'bag' || r.viaItemIds.length) {
      const mech = bagMechanism(w, r, hasRt)
      const viaIds = mech.viaIds
      const via = depth < 2 ? viaIds.map((vid) => ({ itemId: vid, name: itemName(w, vid), sources: resolveItemSources(w, vid, depth + 1).filter((s) => !s.blocked) })) : []
      // Origen de la caja calculado desde sus propias rutas (incluidas las retenidas, que existen en los archivos).
      const allVia = depth < 2 ? viaIds.map((vid) => ({ vid, name: itemName(w, vid), all: resolveItemSources(w, vid, depth + 1) })) : []
      const anyVia = via.some((v) => v.sources.some((s) => s.status !== 'disabled' && !s.pending))
      const notes = [...base.techNotes, mech.note]
      via.forEach((v) => {
        const active = v.sources.filter((s) => s.status !== 'disabled')
        notes.push(`Caja ${v.itemId} (${v.name}): ${v.sources.length} rutas, ${active.length} activas, ${active.filter((s) => !s.pending).length} con origen confirmado.`)
      })
      const boxWhy = allVia
        .map(({ vid, name, all }) => {
          if (!all.length) return `${name} (${vid}) no tiene rutas en 10_drop_routes`
          if (all.every((s) => s.blocked)) return `${name} (${vid}) solo tiene orígenes en revisión`
          if (all.filter((s) => !s.blocked).every((s) => s.status === 'disabled')) return `${name} (${vid}) solo tiene rutas desactivadas por configuración`
          return `${name} (${vid}) tiene rutas activas sin objetivos`
        })
        .join('; ')
      const pending = !hasRt && !anyVia
      return {
        ...base,
        via,
        mechanism: mech.mechanism,
        pending,
        pendingReason: pending
          ? viaIds.length
            ? `La ruta no tiene filas en route_targets.csv y la caja intermedia no tiene origen publicable: ${boxWhy || viaIds.join(', ')}.`
            : `Bolsa ${r.bagId ?? '—'}: sin filas en route_targets.csv, sin via_item_ids y sin activador en 17_bag_triggers.`
          : undefined,
        techNotes: notes,
      }
    }

    if (r.kind === 'event') {
      const m = !hasRt && r.description ? derived(w).mapByName.get(nameKey(r.description)) : undefined
      return {
        ...base,
        eventMapId: m?.id,
        pending: !hasRt && !m,
        pendingReason: !hasRt && !m ? `La ruta de evento no tiene filas en route_targets.csv ni un mapa con el nombre "${r.description ?? ''}".` : undefined,
        techNotes: [...base.techNotes, ...(!hasRt ? [m ? `Mapa del evento por nombre exacto: ${m.name} (id ${m.id}).` : `Sin mapa con el nombre "${r.description ?? ''}" en maps.csv.`] : [])],
      }
    }

    if (r.kind === 'natural') {
      if (hasRt) return base
      return {
        ...base,
        pendingReason: `Ruta natural sin filas en route_targets.csv (drop_level del ítem ${item?.dropLevel ?? '—'}). No se deducen monstruos por nivel.`,
        techNotes: [...base.techNotes, `FALTA: la ruta natural ${r.id} no tiene objetivos en route_targets.csv.`],
      }
    }

    return hasRt ? base : { ...base, pendingReason: `Tipo de ruta "${r.kind}" sin filas en route_targets.csv.`, techNotes: [...base.techNotes, `Tipo de ruta "${r.kind}" sin relación documentada a monstruos.`] }
  })
}

/**
 * Mecanismo de una ruta de bolsa, determinado por los datos:
 * - via_item_ids → caja intermedia que se abre.
 * - Sin via pero con objetivos (route_targets) o activador por monstruo → recompensa interna al
 *   derrotarlo. No necesita caja intermedia y NO es un error.
 * - Sin via ni objetivos, con activador por ítem → ese ítem es la caja que se abre.
 * Solo se exige un ítem intermedio cuando la obtención realmente requiere abrir algo.
 */
export function bagMechanism(w: WorldData, r: DropRoute, hasTargets: boolean): { mechanism?: 'box' | 'internal'; viaIds: string[]; note: string } {
  if (r.viaItemIds.length) return { mechanism: 'box', viaIds: r.viaItemIds, note: `Mecanismo: caja intermedia (via_item_ids ${r.viaItemIds.join('|')}).` }
  const trig = r.bagId !== undefined ? w.idx.bagTriggersByBag.get(r.bagId) ?? [] : []
  const byMonster = trig.filter((t) => t.monsterId !== undefined)
  const byItem = trig.filter((t) => t.itemId && t.monsterId === undefined)
  if (hasTargets || byMonster.length) {
    return { mechanism: 'internal', viaIds: [], note: `Mecanismo: recompensa interna al derrotar al monstruo (${byMonster.length} activadores por monstruo en 17_bag_triggers${hasTargets ? ', objetivos en route_targets' : ''}). No requiere caja intermedia.` }
  }
  if (byItem.length) {
    const ids = Array.from(new Set(byItem.map((t) => {
      const vid = t.itemLevel !== undefined ? `${t.itemId}@${t.itemLevel}` : undefined
      return vid && w.variants.has(vid) ? vid : t.itemId!
    })))
    return { mechanism: 'box', viaIds: ids, note: `Mecanismo: se abre un ítem (activador por ítem en 17_bag_triggers: ${ids.join(', ')}).` }
  }
  return { mechanism: undefined, viaIds: [], note: `FALTA: bolsa ${r.bagId ?? '—'} sin via_item_ids, sin objetivos y sin activador en 17_bag_triggers.` }
}

/** Una ruta está desactivada si verified_possible = False o su regla tiene todas las tasas en cero. */
export function routeDisabled(w: WorldData, r: DropRoute): boolean {
  if (r.verifiedPossible === false) return true
  if (r.kind === 'rule' && r.ruleId) {
    const rule = w.rules.get(r.ruleId)
    if (rule) {
      const defined = rule.rates.filter((x): x is number => x !== undefined)
      if (defined.length && defined.every((x) => x === 0)) return true
    }
  }
  return false
}

export type MonsterItemDrop = {
  itemKey: string
  name: string
  routes: { route: DropRoute; mapIds: number[]; indirect: boolean; eventIds: string[]; disabled: boolean }[]
  indirect: boolean
  disabled: boolean
}

/** Búsqueda inversa: ítems cuyas rutas nombran a este monstruo en route_targets.csv. */
export function monsterItemDrops(w: WorldData, monsterId: number): MonsterItemDrop[] {
  const byItem = new Map<string, MonsterItemDrop>()
  const byRoute = new Map<string, { mapIds: Set<number>; indirect: boolean; eventIds: Set<string> }>()
  ;(w.idx.routeTargetsByMonster.get(monsterId) ?? []).forEach((t) => {
    const r = w.idx.routeById.get(t.routeId)
    // Asociaciones de invasión retenidas (conflicto entre fuentes): no se muestran al jugador.
    if (r && targetWithheld(w, r, t)) return
    // Mismo criterio que la ficha del ítem: todas las rutas salvo desactivadas o retenidas.
    if (!r || !playerRouteVisible(w, r, t)) return
    const e = byRoute.get(t.routeId) ?? { mapIds: new Set<number>(), indirect: false, eventIds: new Set<string>() }
    if (t.mapId !== undefined) e.mapIds.add(t.mapId)
    if (t.isIndirect) e.indirect = true
    if (t.eventId) e.eventIds.add(t.eventId)
    byRoute.set(t.routeId, e)
  })
  byRoute.forEach((info, routeId) => {
    const route = w.idx.routeById.get(routeId)
    if (!route) return
    // Solo se enlaza a la variante si existe en el catálogo de variantes; si no, al ítem base.
    const itemKey = route.variantId && w.variants.has(route.variantId) ? route.variantId : route.itemId
    if (!itemKey) return
    const disabled = routeDisabled(w, route)
    const cur = byItem.get(itemKey) ?? { itemKey, name: itemName(w, itemKey), routes: [], indirect: true, disabled: true }
    cur.routes.push({ route, mapIds: Array.from(info.mapIds), indirect: info.indirect, eventIds: Array.from(info.eventIds), disabled })
    if (!info.indirect) cur.indirect = false
    if (!disabled) cur.disabled = false
    byItem.set(itemKey, cur)
  })
  return Array.from(byItem.values()).sort((a, b) => Number(a.disabled) - Number(b.disabled) || Number(a.indirect) - Number(b.indirect) || a.name.localeCompare(b.name, 'es'))
}

function walk(list: ResolvedSource[], fn: (s: ResolvedSource) => void) {
  list.forEach((s) => {
    if (s.status === 'disabled' || s.blocked) return
    fn(s)
    s.via?.forEach((v) => walk(v.sources, fn))
  })
}

/** Monstruos confirmados (por ID) en rutas no desactivadas, incluidas las cajas. */
export function confirmedMonsterIds(sources: ResolvedSource[]): number[] {
  const set = new Set<number>()
  walk(sources, (s) => s.targets.forEach((t) => set.add(t.monsterId)))
  return Array.from(set)
}

export const confirmedMonsterCount = (sources: ResolvedSource[]) => confirmedMonsterIds(sources).length

/**
 * Clasificación global de un ítem para el panel técnico:
 * - documented: alguna ruta activa con monstruo y coordenadas (o mapa de premio de evento).
 * - partial: rutas activas, pero sin coordenadas o con objetivos pendientes.
 * - all-disabled: todas sus rutas están desactivadas.
 * - no-routes: sin filas en drop_routes.
 */
export type ItemCoverage = 'documented' | 'partial' | 'review' | 'all-disabled' | 'no-routes'

/** Qué falta exactamente en un ítem "partial" (o por qué está en revisión). */
export type CoverageDetail = 'coords' | 'held' | 'box' | 'missing'

export function itemCoverage(sources: ResolvedSource[]): { coverage: ItemCoverage; reason?: string; detail?: CoverageDetail } {
  if (!sources.length) return { coverage: 'no-routes', reason: 'Sin filas en drop_routes.csv para este item_id (no implica que no esté disponible en el servidor).' }
  const visible = sources.filter((s) => !s.blocked)
  const heldRows = sources.reduce((n, s) => n + s.heldTargetRows, 0)
  if (!visible.length) return { coverage: 'review', reason: `En revisión: sus rutas tienen ${heldRows} objetivos documentados, retenidos hasta aprobar la recompensa de invasión.` }
  const active = visible.filter((s) => s.status !== 'disabled')
  if (!active.length) {
    return sources.some((s) => s.blocked)
      ? { coverage: 'review', reason: 'Sus rutas publicables están desactivadas y las demás están en revisión.' }
      : { coverage: 'all-disabled', reason: 'Todas sus rutas están desactivadas (verified_possible = False o tasas en cero).' }
  }
  let located = false
  walk(active, (s) => {
    if (s.eventMaps.length || s.eventMapId !== undefined) located = true
    if (s.targets.some((t) => t.spawns.length > 0)) located = true
  })
  if (located) return { coverage: 'documented' }
  let anyTarget = false
  walk(active, (s) => {
    if (s.targets.length) anyTarget = true
  })
  if (anyTarget) return { coverage: 'partial', detail: 'coords', reason: `Monstruo y mapa documentados; faltan coordenadas o reglas de aparición para ese monstruo en ese mapa y contexto.${heldRows ? ` Además, ${heldRows} objetivos en revisión.` : ''}` }
  if (heldRows > 0 || sources.some((s) => s.blocked)) return { coverage: 'review', detail: 'held', reason: `Las rutas publicables no tienen objetivos; ${heldRows} objetivos documentados están en revisión.` }
  const boxSource = active.find((s) => s.via?.length && s.pendingReason)
  if (boxSource) return { coverage: 'partial', detail: 'box', reason: boxSource.pendingReason }
  const why = active.find((s) => s.pendingReason)?.pendingReason
  return { coverage: 'partial', detail: 'missing', reason: why ?? 'Rutas activas sin monstruo, evento ni caja con origen.' }
}

export function sourceSummary(sources: ResolvedSource[]) {
  return {
    total: sources.length,
    active: sources.filter((s) => s.status === 'active').length,
    partial: sources.filter((s) => s.status === 'partial').length,
    disabled: sources.filter((s) => s.status === 'disabled').length,
    pending: sources.filter((s) => s.status !== 'disabled' && s.pending).length,
  }
}
